import type { PredictorConfig } from "../engine/config";
import type { IsoDateTime } from "../engine/dates";
import type { Bean } from "../engine/model";
import { activeNudges, nudgeState, tasteNudge, type NudgeState } from "../engine/tasteNudge";
import type { Repositories, StoredNudge } from "../storage/repositories";
import { formatSeconds } from "./format";

/**
 * Taste nudges on a bean's target time: the stored log is the state (see engine/tasteNudge). The app evaluates the
 * rule after every saved or edited shot on the selected bean, and the bean menu can undo the latest nudge.
 */
export async function nudgeStateFor(repos: Repositories, beanId: number): Promise<NudgeState> {
  return nudgeState(await repos.nudges.findByBean(beanId));
}

/** Apply the rule; returns the stored nudge when one was made. `cfg` is the active config (recipe applied). */
export async function evaluateNudge(repos: Repositories, bean: Bean, now: IsoDateTime, cfg: PredictorConfig): Promise<StoredNudge | null> {
  if (bean.id === null) return null;
  const log = await repos.nudges.findByBean(bean.id);
  const d = tasteNudge(bean, await repos.shots.findByBean(bean.id), log, now, cfg);
  if (d.nudge === null) return null;
  return repos.nudges.save({ id: null, beanId: bean.id, createdAt: now, deltaS: d.nudge.deltaS, taggedCount: d.nudge.taggedCount,
                             reason: d.nudge.reason, undone: false });
}

/** Undo the latest active nudge on a bean; returns it, or null when there was none. */
export async function undoLatestNudge(repos: Repositories, beanId: number): Promise<StoredNudge | null> {
  const log = await repos.nudges.findByBean(beanId);
  const active = activeNudges(log) as StoredNudge[];
  const last = active[active.length - 1];
  if (last === undefined) return null;
  return repos.nudges.save({ ...last, undone: true });
}

/** "Target nudged to 28.5 s — recent shots tasted sour". */
export function nudgeMessage(n: Pick<StoredNudge, "reason">, cfg: PredictorConfig, offsetS: number): string {
  return `Target nudged to ${formatSeconds(cfg.targetTimeS + offsetS)} s — recent shots tasted ${n.reason}`;
}
